/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { arrayOf, bool, element, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import YdtClose from '../../assets/icons/action/close.svg';
import YdtErrorFill from '../../assets/icons/alert/error-fill.svg';
import YdtSuccessCheck from '../../assets/icons/alert/success-check.svg';
import YdtWarningFill from '../../assets/icons/alert/warning-fill.svg';
import Button from '../Button';
import Icon from '../Icon';
import Text from '../Text';
import * as styles from './dialog.css';
import { dt } from '../../helpers/Utils/GetTailwindToken';

/**
 * Dialog (modal) component. Any props not present in `propTypes` will be spread onto the `[data-core-dialog]` element.
 * @link https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 */
const Dialog = (props) => {
    const {
        automationId,
        body,
        buttons,
        className,
        description,
        headingIcon,
        onDismiss,
        show,
        subtitle,
        title,
        ...attrs
    } = props;

    const labelID = `dialog-label-${Date.now()}`;
    const refClose = useRef(null);
    const refDialog = useRef(null);
    const refDialogSiblings = useRef(null);
    const refOriginalFocus = useRef(null);
    const refSrollPos = useRef([0, 0]);
    const refTitle = useRef(null);
    const refTrapIgnoreFocus = useRef(false);
    const refTrapLastFocus = useRef(null);

    /* istanbul ignore next */
    const dismissHandler = useCallback(
        (event) => {
            event.stopPropagation();
            document.body.classList.remove('overflow-hidden');
            onDismiss && onDismiss(event);
        },
        [onDismiss]
    );

    /* istanbul ignore next */
    const onEscape = useCallback(
        (event) => {
            if (event.key !== 'Escape') return;

            dismissHandler(event);
        },
        [dismissHandler]
    );

    /* istanbul ignore next */
    const onFocus = useCallback((event) => {
        if (refTrapIgnoreFocus.current) return;

        const setFocus = (element) => {
            refTrapIgnoreFocus.current = true;
            element.current && element.current.focus();
            refTrapIgnoreFocus.current = false;
        };

        if (refDialog.current.contains(event.target)) {
            refTrapLastFocus.current = event.target;
        } else {
            setFocus(refTitle);

            if (refTrapLastFocus.current === document.activeElement) {
                setFocus(refClose);
            }
            refTrapLastFocus.current = document.activeElement;
        }
    }, []);

    /* istanbul ignore next */
    const onScroll = useCallback(() => {
        window.scrollTo(...refSrollPos.current);
    });

    const effectListeners = () => {
        if (show) {
            document.addEventListener('focus', onFocus, true);
            document.addEventListener('keyup', onEscape, true);
            document.addEventListener('scroll', onScroll, { passive: false });
        }

        return () => {
            if (show) {
                document.removeEventListener('focus', onFocus, true);
                document.removeEventListener('keyup', onEscape, true);
                document.removeEventListener('scroll', onScroll, { passive: false });
            }
        };
    };

    const effectOrignalFocus = () => {
        refOriginalFocus.current = document.activeElement;
        return () => {
            refOriginalFocus.current && refOriginalFocus.current.focus();
        };
    };

    const effectFocusIntitial = () => {
        if (!show) return;

        if (title) document.getElementById(labelID).focus();
        else if (refDialog.current) refDialog.current.focus();
    };

    const effectToggleAria = () => {
        if (show) {
            refDialogSiblings.current = [...document.body.children].filter(
                (sibling) => sibling !== refDialog.current.parentNode
            );
        }

        show &&
            refDialogSiblings.current.forEach((element) =>
                element.setAttribute('aria-hidden', true)
            );

        return () =>
            show &&
            refDialogSiblings.current.forEach((element) => element.removeAttribute('aria-hidden'));
    };

    const effectDisableBackgroundScroll = () => {
        if (show) document.body.classList.add('overflow-hidden');
    };

    const effectSetScrollPosition = () => {
        if (show) refSrollPos.current = [window.scrollX, window.scrollY];
    };

    const dialogConfig = {
        closeButton: {
            'aria-label': 'Close',
            children: (
                <Icon height="24" width="24">
                    <YdtClose />
                </Icon>
            ),
            className: [dt(['absolute', 'mr-1', 'mt-1', 'right-6'])],
            onClick: dismissHandler,
            ref: refClose,
        },
        bodyContent: {
            className: [dt(['flex-grow', 'mb-1', 'overflow-y-auto', 'text-left'])],
        },
        headingContent: {
            className: [dt(['mb-1', 'text-left'])],
        },
        content: {
            className: [dt(['flex', 'flex-col', 'flex-grow', 'min-h-0'])],
            'data-core-dialog-content': true,
        },
        contentWrapper: {
            'aria-labelledby': title ? labelID : null,
            'aria-modal': 'true',
            className: [
                dt([
                    'bg-white',
                    'flex',
                    'flex-col',
                    'p-6',
                    'pb-3',
                    'relative',
                    'rounded-medium',
                    'max-h-[480px]',
                    'md:w-[416px]',
                    'w-80',
                ]),
                styles.dialog,
                className,
            ].join(' '),
            'data-automation-id': automationId,
            'data-core-dialog': true,
            ref: refDialog,
            role: 'dialog',
            tabIndex: '-1',
            ...attrs,
        },
        icon: {
            error: {
                children: <YdtErrorFill />,
                className: [dt(['mb-1', 'text-error'])],
                height: '32',
                id: 'ydt-error-fill',
                width: '32',
            },
            success: {
                children: <YdtSuccessCheck />,
                className: [dt(['mb-1', 'text-success'])],
                height: '32',
                id: 'ydt-success-check',
                width: '32',
            },
            warning: {
                children: <YdtWarningFill />,
                className: [dt(['mb-1', 'text-urgency'])],
                height: '32',
                id: 'ydt-warning-fill',
                width: '32',
            },
        },
        overlay: {
            className: [
                styles['dialog-overlay'],
                dt([
                    'bg-overlay-60',
                    'fixed',
                    'flex',
                    'items-center',
                    'justify-center',
                    'z-50',
                    'bottom-0',
                    'left-0',
                    'right-0',
                    'top-0',
                ]),
            ].join(' '),
            onClick: dismissHandler,
        },
    };

    // These are ordered in alignment to the user interaction model.
    useEffect(effectListeners, [onFocus, onEscape]);
    useEffect(effectOrignalFocus, [show, refOriginalFocus]);
    useEffect(effectFocusIntitial, [show, title, refDialog]);
    useEffect(effectToggleAria, [show]);
    useEffect(effectSetScrollPosition, [show]);
    useEffect(effectDisableBackgroundScroll, [show]);

    const dialogButtonStyle = dt(['mt-2', 'w-full', 'right-0', 'top-0', 'mx-2']);
    const buttonContainerStyle = dt(['mt-2', 'justify-center']);
    const Buttons = () =>
        !!buttons.length && (
            <div className={buttonContainerStyle} data-core-dialog-actions>
                {buttons.map((btn) => (
                    <Button className={dialogButtonStyle} key={btn.children.toString()} {...btn} />
                ))}
            </div>
        );

    const Heading = () =>
        !!(subtitle || title) && (
            <header {...dialogConfig.headingContent}>
                {dialogConfig.icon[headingIcon] && <Icon {...dialogConfig.icon[headingIcon]} />}
                {title && (
                    <Text
                        align="left"
                        className="mb-4"
                        id={labelID}
                        preset="title"
                        ref={refTitle}
                        size="medium"
                        tabIndex="-1"
                        tag="h2"
                    >
                        {title}
                    </Text>
                )}
                {subtitle && (
                    <Text align="left" preset="body">
                        {subtitle}
                    </Text>
                )}
            </header>
        );

    const Body = () =>
        !!(description || body) && (
            <div {...dialogConfig.bodyContent} data-core-dialog-body>
                {description && (
                    <Text align="left" preset="body">
                        {description}
                    </Text>
                )}
                {body}
            </div>
        );

    return (
        show &&
        createPortal(
            <div {...dialogConfig.overlay}>
                <div tabIndex="0" />
                <div {...dialogConfig.contentWrapper}>
                    <div {...dialogConfig.content}>
                        <Heading />
                        <Body />
                        <Buttons />
                    </div>
                    <button {...dialogConfig.closeButton} type="button" />
                </div>
                <div tabIndex="0" />
            </div>,
            document.body
        )
    );
};

Dialog.propTypes = {
    /** Automation testing ID. */
    automationId: string,

    /** The node(s) to render in the component. */
    body: node,

    /** CTA buttons. See `<Button/>` component for prop options. */
    buttons: arrayOf(
        shape({
            /** Automation ID */
            automationId: string,
            /** The text or node(s) to render in the component. */
            children: node,
            /** Additional classes to add to button */
            className: string,
            /** Disables the button when true */
            disabled: bool,
            /** URL to use for the `href` value on an `<a/>` element */
            href: string,
            /** Icon to use on button, such as 'ydt-lock' */
            icon: string,
            /** Icon to use on button, such as 'ydt-lock' */
            iconComponent: element,
            /** Displays loading animation when true */
            loading: bool,
            /** Button theme */
            buttonType: oneOf(['Primary', 'Secondary', 'Tertiary', 'Text']),
            /** Button size */
            size: oneOf(['Md', 'Lg']),
            /** Button `type` attribute */
            type: oneOf(['button', 'submit']),
        })
    ),

    /** Custom classes added to Dialog wrapper. */
    className: string,

    /** Textual content that describes the action the user is to consider. */
    description: oneOfType([element, string]),

    /** Heading icon to display. */
    headingIcon: oneOf(['error', 'success', 'warning']),

    /**
     * Function called when user attemps to close the dialog by one of the
     * following actions:
     *   - Using the "Escape" key.
     *   - Using the "Close" button.
     *
     * If provided, this function is responsible for closing the dialog by
     * setting the `show` prop to `false`.
     */
    onDismiss: func,

    /** Whether or not to show the dialog. */
    show: bool,

    /** Content subtitle. */
    subtitle: string,

    /** Content title. */
    title: (props, componentName) => {
        if (!props.title && props['aria-label'] && typeof props['aria-label'] === 'string')
            return null;

        if (!props.title) {
            return new Error(
                [
                    'Invalid prop `title` with value',
                    `\`${props.title}\` supplied to \`${componentName}\`.`,
                    'When `title` is not provided, the attribute `aria-label` must be provided for accessibility.',
                    'Validation failed.',
                ].join(' ')
            );
        }

        return null;
    },
};

Dialog.defaultProps = {
    automationId: null,
    body: null,
    buttons: [],
    className: '',
    description: null,
    onDismiss: null,
    show: false,
    subtitle: null,
    title: null,
};

export default Dialog;
